import React, { useState, useRef, useEffect } from 'react';
import {
  Check, ChevronDown, ChevronRight, Plus, X, Calendar as CalendarIcon,
  Clock, Upload, Search, ClipboardList, CheckCircle2, ListTodo, Hourglass, Paperclip, User,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { getInternalUsers } from '../services/userService';
import { getTasksForPortalUser, createTask, updateTask, deleteTask, toggleTaskCompletion, uploadTaskFile } from '../services/taskService';
import { useTaskTime } from '../hooks/useTaskTime';
import { formatTaskTimeHours } from '../services/taskTimeService';
import {
  TASK_TIME_STATUS,
  taskTimeDepartmentLabel,
  taskTimeRequestHours,
} from '../utils/taskTimeConstants';
import DepartmentTaskTimeModal from '../components/portal/DepartmentTaskTimeModal';
import TaskTimeStatusBadge from '../components/taskTime/TaskTimeStatusBadge';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import PortalBackground from '../components/PortalBackground';

const isFathomTask = (task) => task?.source === 'fathom' || !!task?.fathomRecordingId;

const isOpenImplementationTask = (req) =>
  req.status !== TASK_TIME_STATUS.COMPLETED && req.status !== TASK_TIME_STATUS.DENIED;

const TAB_META = {
  'TO DO': {
    label: 'To Do',
    icon: ListTodo,
    tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    countText: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-2 ring-amber-400 dark:ring-amber-500',
  },
  'IMPLEMENTATION': {
    label: 'Implementation Tasks',
    icon: Hourglass,
    tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    countText: 'text-red-600 dark:text-red-400',
    ring: 'ring-2 ring-red-400 dark:ring-red-500',
  },
  'DONE': {
    label: 'Done',
    icon: CheckCircle2,
    tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    countText: 'text-emerald-600 dark:text-emerald-400',
    ring: 'ring-2 ring-emerald-400 dark:ring-emerald-500',
  },
};

// Avatar component that shows profile picture or initials
const UserAvatar = ({ user, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-sm',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  const baseClasses = `rounded-full flex items-center justify-center font-medium ${sizeClasses[size]} ${className}`;

  if (user.profileImage) {
    return (
      <div className={`${baseClasses} overflow-hidden`}>
        <img
          src={user.profileImage}
          alt={user.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300" style={{ display: 'none' }}>
          {user.initials}
        </div>
      </div>
    );
  }

  return (
    <div className={`${baseClasses} bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300`}>
      {user.initials}
    </div>
  );
};

const PortalTasks = () => {
  const { currentUser } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const taskTime = useTaskTime();

  // States
  const [tasks, setTasks] = useState([]);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [implModal, setImplModal] = useState(null); // { tab: 'tasks' | 'new' }
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [activeTab, setActiveTab] = useState('TO DO');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [clientUsers, setClientUsers] = useState({}); // Store client user info by user ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [tasksToShow, setTasksToShow] = useState(5);

  // New task state
  const [newTask, setNewTask] = useState({
    title: '',
    assignedTo: [],
    description: '',
    completed: false,
    files: [],
    showTimePopup: false
  });

  // Add ref for dropdown
  const assigneeDropdownRef = useRef(null);

  // Load tasks and internal users on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Load user profile data
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
        }

        // Load internal users for assignment first
        const users = await getInternalUsers();

        // Only add current user to the internal users list if they are actually an internal user
        const userExists = users.find(user => user.id === currentUser.uid);
        if (userExists) {
          // Current user is already in the internal users list
          setInternalUsers(users);
        } else {
          // Check if current user is an internal user before adding them
          const userDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (userDoc.exists()) {
            // Current user is an internal user, add them to the list
            const currentUserData = {
              id: currentUser.uid,
              firstName: userProfile?.firstName || currentUser.displayName?.split(' ')[0] || currentUser.email?.split('@')[0] || '',
              lastName: userProfile?.lastName || currentUser.displayName?.split(' ').slice(1).join(' ') || '',
              email: currentUser.email,
              profileImage: userProfile?.profileImage || currentUser.photoURL
            };
            setInternalUsers([...users, currentUserData]);
          } else {
            // Current user is not an internal user, only show existing internal users
            setInternalUsers(users);
          }
        }

        // Load tasks assigned to the current user (exclude internal-only tasks)
        const clientAccountId = userDoc.exists() ? userDoc.data()?.accountId : null;
        const userTasks = await getTasksForPortalUser(currentUser.uid, clientAccountId);

        // Load client user information for task creators and assignees
        const clientUserInfo = {};
        for (const task of userTasks) {
          // Load creator info if not internal user
          if (task.createdBy && task.createdBy !== 'Internal') {
            const isInternalUser = internalUsers.find(u => u.id === task.createdBy);
            if (!isInternalUser) {
              try {
                const userDoc = await getDoc(doc(db, 'users', task.createdBy));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  if (userData.accountId) {
                    // Get account info for display
                    const accountDoc = await getDoc(doc(db, 'accounts', userData.accountId));
                    if (accountDoc.exists()) {
                      const accountData = accountDoc.data();
                      clientUserInfo[task.createdBy] = {
                        id: task.createdBy,
                        name: accountData.company || 'Client',
                        initials: accountData.company?.[0] || 'C',
                        type: 'client'
                      };
                    }
                  }
                }
              } catch (error) {
                console.error('Error loading client user info:', error);
              }
            }
          }

          // Load assignee info for client users
          if (task.assignees) {
            for (const assigneeId of task.assignees) {
              const isInternalUser = internalUsers.find(u => u.id === assigneeId);
              if (!isInternalUser && !clientUserInfo[assigneeId]) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', assigneeId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    if (userData.accountId) {
                      const accountDoc = await getDoc(doc(db, 'accounts', userData.accountId));
                      if (accountDoc.exists()) {
                        const accountData = accountDoc.data();
                        clientUserInfo[assigneeId] = {
                          id: assigneeId,
                          name: accountData.company || 'Client',
                          initials: accountData.company?.[0] || 'C',
                          type: 'client'
                        };
                      }
                    }
                  }
                } catch (error) {
                  console.error('Error loading client assignee info:', error);
                }
              }
            }
          }
        }
        setClientUsers(clientUserInfo);

        // Process assignees to include full user details
        const processedTasks = userTasks.map(task => {
          const assigneesWithDetails = (task.assignees || []).map(assigneeId => {
            // First check internal users
            const internalUser = internalUsers.find(u => u.id === assigneeId);
            if (internalUser) {
              return {
                id: internalUser.id,
                name: internalUser.firstName && internalUser.lastName
                  ? `${internalUser.firstName} ${internalUser.lastName}`
                  : internalUser.firstName
                  ? internalUser.firstName
                  : internalUser.email?.split('@')[0] || 'Unknown',
                initials: internalUser.firstName && internalUser.lastName
                  ? `${internalUser.firstName[0]}${internalUser.lastName[0]}`.toUpperCase()
                  : internalUser.firstName
                  ? internalUser.firstName.substring(0, 2).toUpperCase()
                  : (internalUser.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
                profileImage: internalUser.profileImage
              };
            }

            // Then check client users
            const clientUser = clientUserInfo[assigneeId];
            if (clientUser) {
              return {
                id: clientUser.id,
                name: clientUser.name,
                initials: clientUser.initials,
                type: 'client'
              };
            }

            return null;
          }).filter(Boolean);

          return {
            ...task,
            assignees: assigneesWithDetails
          };
        });

        setTasks(processedTasks);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setShowAssigneeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateTask = async () => {
    if (!currentUser) return;

    try {
      // Validate required fields
      if (!newTask.title.trim()) {
        setError('Task title is required');
        return;
      }

      // Get client's account ID
      let clientAccountId = 'portal'; // Default fallback
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accountId) {
            clientAccountId = userData.accountId;
          }
        }
      } catch (error) {
        console.error('Error getting client account ID:', error);
      }

      // Create task data (without files first)
      // Portal users can only create tasks for themselves.
      const taskData = {
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        assignees: [currentUser.uid], // Always self-assigned
        accountId: clientAccountId, // Use client's actual account ID
        completed: false,
        status: 'Not Done',
        files: [], // Start with empty files array
        createdBy: currentUser.uid // Add the creator field
      };

      // Create task in Firestore
      const createdTask = await createTask(taskData);

      // Upload files if any
      if (newTask.files && newTask.files.length > 0) {
        try {
          const uploadPromises = newTask.files.map(file => uploadTaskFile(file, createdTask.id));
          const uploadedFiles = await Promise.all(uploadPromises);

          // Update the task with uploaded file information
          await updateTask(createdTask.id, { files: uploadedFiles });
          createdTask.files = uploadedFiles;
        } catch (fileError) {
          console.error('Error uploading files:', fileError);
          setError(`Task created but failed to upload files: ${fileError.message}`);
        }
      }

      // Task is always self-assigned, so add it to local state
      setTasks(prevTasks => [...prevTasks, createdTask]);

      // Reset form
      setNewTask({
        title: '',
        assignedTo: [],
        description: '',
        completed: false,
        files: [],
        showTimePopup: false
      });

      setShowNewTaskModal(false);
      setError(null);
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Failed to create task. Please try again.');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;


      // Only allow deletion if user is the task creator
      if (task.createdBy !== currentUser.uid) {
        setError('You can only delete tasks that you created.');
        return;
      }

      await deleteTask(taskId);
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      setShowTaskModal(false);
      setSelectedTask(null);
      setError(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      setError('Failed to delete task. Please try again.');
    }
  };

  const handleUpdateTask = async (updatedTask) => {
    try {
      const originalTask = tasks.find(t => t.id === updatedTask.id);
      if (!originalTask) return;


      // Only allow editing if user is the task creator
      if (originalTask.createdBy !== currentUser.uid) {
        setError('You can only edit tasks that you created.');
        return;
      }

      const updated = await updateTask(updatedTask.id, updatedTask);
      setTasks(prevTasks => prevTasks.map(task =>
        task.id === updatedTask.id ? updated : task
      ));
      setSelectedTask(updated);
      setError(null);
    } catch (error) {
      console.error('Error updating task:', error);
      setError('Failed to update task. Please try again.');
    }
  };

  const handleToggleTaskCompletion = async (taskId, e) => {
    if (e) {
      e.stopPropagation();
    }

    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newCompleted = !task.completed;
    const processedUpdated = {
      ...task,
      completed: newCompleted,
      completedAt: newCompleted ? new Date() : null,
    };

    setTasks(prevTasks => prevTasks.map(t =>
      t.id === taskId ? processedUpdated : t
    ));

    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(processedUpdated);
    }

    try {
      await toggleTaskCompletion(taskId, newCompleted);
      setError(null);
    } catch (error) {
      console.error('Error toggling task completion:', error);
      setTasks(prevTasks => prevTasks.map(t =>
        t.id === taskId ? task : t
      ));
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(task);
      }
      setError('Failed to update task. Please try again.');
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedTask({ ...selectedTask });
  };

  const handleSaveClick = () => {
    handleUpdateTask(editedTask);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTask(null);
  };

  // Filter tasks based on status
  const filteredTasks = tasks.filter(task => {
    if (activeTab === 'TO DO') return !task.completed;
    return task.completed;
  });

  // Get tasks to display based on pagination
  const displayedTasks = filteredTasks.slice(0, tasksToShow);
  const hasMoreTasks = filteredTasks.length > tasksToShow;

  // Handle load more
  const handleLoadMore = () => {
    setTasksToShow(prev => prev + 10);
  };

  // Reset pagination when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTasksToShow(5);
  };

  // Render the "Created by" name for a task card meta line
  const renderCreatorName = (createdBy) => {
    if (createdBy === 'Internal') return 'Internal';
    const internalUser = internalUsers.find(u => u.id === createdBy);
    if (internalUser) {
      return internalUser.firstName && internalUser.lastName
        ? `${internalUser.firstName} ${internalUser.lastName}`
        : internalUser.firstName
        ? internalUser.firstName
        : internalUser.email?.split('@')[0] || 'Unknown';
    }
    const clientUser = clientUsers[createdBy];
    if (clientUser) return clientUser.name;
    return 'Unknown';
  };

  // Format a due date (Firestore Timestamp, Date, or string)
  const formatDueDate = (dueDate) => {
    try {
      // Handle Firestore Timestamp
      if (dueDate.toDate && typeof dueDate.toDate === 'function') {
        return dueDate.toDate().toLocaleDateString();
      }
      // Handle JavaScript Date or string
      const date = new Date(dueDate);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error parsing date:', error, dueDate);
      return 'Invalid Date';
    }
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
    setIsEditing(false);
    setEditedTask(null);
  };

  return (
    <div className={`relative min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-8'}`}>
      <PortalBackground />
      <div className={`relative ${isMobile ? 'w-full' : 'max-w-4xl'} mx-auto`}>
        {/* Header */}
        <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
          <div className={`text-center ${isMobile ? 'px-14' : 'px-32'} py-1`}>
            <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>Tasks</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Keep track of what needs to get done.
            </p>
          </div>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
            <button
              type="button"
              onClick={() => setShowNewTaskModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              {!isMobile && <span>New Task</span>}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Status stat cards — click to switch tab */}
        <div className={`grid grid-cols-3 ${isMobile ? 'gap-2 mb-4' : 'gap-4 mb-6'}`}>
          {Object.entries(TAB_META).map(([tab, meta]) => {
            const Icon = meta.icon;
            const count = tab === 'TO DO'
              ? tasks.filter(t => !t.completed).length
              : tab === 'IMPLEMENTATION'
              ? (taskTime.requests || []).filter(isOpenImplementationTask).length
              : tasks.filter(t => t.completed).length;
            const countLoading = tab === 'IMPLEMENTATION' ? taskTime.loading : loading;
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => handleTabChange(tab)}
                title={`Show ${meta.label.toLowerCase()} tasks`}
                className={`group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  isMobile ? 'p-3' : 'p-4'
                } ${active ? meta.ring : ''}`}
              >
                <div className={`flex items-center ${isMobile ? 'flex-col gap-1.5 text-center' : 'gap-3'}`}>
                  <div className={`${isMobile ? 'w-9 h-9' : 'w-11 h-11'} rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                    <Icon className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold leading-none ${
                      count === 0 ? 'text-gray-300 dark:text-gray-600' : meta.countText
                    }`}>
                      {countLoading ? '…' : count}
                    </p>
                    <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{meta.label}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Search and Filters Row */}
        <div className={`flex ${isMobile ? 'flex-col gap-2 mb-4' : 'items-center gap-3 mb-6'}`}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search tasks..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <select className={`${isMobile ? 'w-full' : 'w-44'} px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}>
            <option value="">All Dates</option>
            <option value="overdue">Overdue</option>
            <option value="this-week">This Week</option>
            <option value="this-month">This Month</option>
            <option value="this-year">This Year</option>
          </select>
        </div>

        {/* Loading State */}
        {(activeTab === 'IMPLEMENTATION' ? taskTime.loading : loading) && (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                  <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Implementation Tasks List — hours-pool requests, managed in the shared modal */}
        {activeTab === 'IMPLEMENTATION' && !taskTime.loading && (
          <div className="space-y-2">
            {taskTime.allowanceHours != null && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-1">
                {formatTaskTimeHours(taskTime.remainingHours)} of {formatTaskTimeHours(taskTime.allowanceHours)} Implementation Hours remaining this period
              </p>
            )}
            {(taskTime.requests || []).length === 0 ? (
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
                <Hourglass className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No implementation tasks yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                  Implementation tasks are work your BBA team completes for you using your Implementation Hours.
                </p>
                <button
                  type="button"
                  onClick={() => setImplModal({ tab: 'new' })}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Implementation Task
                </button>
              </div>
            ) : (
              <>
                {(taskTime.requests || []).slice(0, tasksToShow).map((req) => (
                  <div
                    key={req.id}
                    onClick={() => setImplModal({ tab: 'tasks' })}
                    className={`${isMobile ? 'p-3' : 'p-4'} rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 mt-0.5 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                        <Hourglass className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{req.title}</h3>
                        {req.description && (
                          <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{req.description}</p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">{taskTimeDepartmentLabel(req.department)}</span>
                          {taskTimeRequestHours(req) != null && (
                            <>
                              <span className="text-gray-300 dark:text-gray-600">·</span>
                              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                <Clock className="w-3 h-3 shrink-0" />
                                {formatTaskTimeHours(taskTimeRequestHours(req))}
                              </span>
                            </>
                          )}
                          {Array.isArray(req.attachments) && req.attachments.length > 0 && (
                            <>
                              <span className="text-gray-300 dark:text-gray-600">·</span>
                              <span className="inline-flex items-center gap-1">
                                <Paperclip className="w-3 h-3 shrink-0" />
                                {req.attachments.length}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <TaskTimeStatusBadge status={req.status} />
                        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                      </div>
                    </div>
                  </div>
                ))}
                {(taskTime.requests || []).length > tasksToShow && (
                  <div className="flex justify-center pt-4">
                    <button
                      onClick={handleLoadMore}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Load More ({(taskTime.requests || []).length - tasksToShow} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tasks List */}
        {activeTab !== 'IMPLEMENTATION' && !loading && (
          <div className="space-y-2">
            {filteredTasks.length === 0 ? (
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
                <ClipboardList className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  No {activeTab === 'TO DO' ? 'pending' : 'completed'} tasks
                </p>
                {activeTab === 'TO DO' && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                    You're all caught up. Create a task to get started.
                  </p>
                )}
              </div>
            ) : (
              displayedTasks.map(task => (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className={`${isMobile ? 'p-3' : 'p-4'} rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={(e) => handleToggleTaskCompletion(task.id, e)}
                      title={task.completed ? 'Mark as not done' : 'Mark as done'}
                      className={`w-5 h-5 mt-0.5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                        task.completed
                          ? 'bg-emerald-500 border-emerald-500'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-emerald-500 dark:hover:border-emerald-500'
                      }`}
                    >
                      {task.completed && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm font-medium ${task.completed ? 'line-through opacity-60' : ''} text-gray-900 dark:text-gray-100`}>
                        {task.title}
                      </h3>
                      {task.description && (
                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        {task.estimatedHours > 0 && (
                          <>
                            <span className="inline-flex items-center gap-1 whitespace-nowrap">
                              <Clock className="w-3 h-3 shrink-0" />
                              {task.estimatedHours}h {task.estimatedMinutes}m
                            </span>
                            <span className="text-gray-300 dark:text-gray-600">·</span>
                          </>
                        )}
                        {/* Created by information */}
                        <span className="inline-flex items-center gap-1 min-w-0">
                          {isFathomTask(task) ? (
                            <>
                              <img src="/fathomlogo.png" alt="Fathom" className="w-3.5 h-3.5 object-contain shrink-0" />
                              <span className="truncate">Created by Fathom</span>
                            </>
                          ) : (
                            <span className="truncate">Created by {renderCreatorName(task.createdBy)}</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Due date positioned on the right side */}
                      {task.dueDate && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          <CalendarIcon className="w-3 h-3" />
                          {formatDueDate(task.dueDate)}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Load More Button */}
            {hasMoreTasks && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={handleLoadMore}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Load More ({filteredTasks.length - tasksToShow} remaining)
                </button>
              </div>
            )}
          </div>
        )}

        {/* New Task Modal */}
        {showNewTaskModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewTaskModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setShowNewTaskModal(false)}
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                    <ClipboardList className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Task</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Assign yourself a task, or send one to your BBA team.</p>
                </div>
              </div>

              {/* Modal Body */}
              <div className={isMobile ? 'p-4' : 'p-6'}>
                {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>}

                {/* Task type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Task type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-start gap-2.5 p-3 rounded-lg border-2 border-red-400 dark:border-red-500 bg-red-50/50 dark:bg-red-900/10 text-left">
                      <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">My Task</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">A to-do you complete yourself.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewTaskModal(false);
                        setImplModal({ tab: 'new' });
                      }}
                      className="flex items-start gap-2.5 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                        <Hourglass className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Implementation Task</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Your BBA team does it — uses your Implementation Hours.</p>
                      </div>
                    </button>
                  </div>
                </div>
                {/* Title Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Task Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter task title"
                  />
                </div>

                {/* Description Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Description
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder="Enter task description"
                  />
                </div>



                {/* Attachments Section */}
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Attachments
                  </label>
                  <div className="space-y-3">
                    {/* File Upload Input */}
                    <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <Upload className="w-4 h-4" />
                      <span>Choose files to attach</span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          setNewTask(prev => ({
                            ...prev,
                            files: [...prev.files, ...newFiles]
                          }));
                        }}
                      />
                    </label>

                    {/* Selected Files Display */}
                    {newTask.files && newTask.files.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Selected files ({newTask.files.length}):
                        </div>
                        {newTask.files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                            <div className="flex items-center gap-2 min-w-0">
                              <Upload className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              onClick={() => setNewTask(prev => ({
                                ...prev,
                                files: prev.files.filter((_, i) => i !== index)
                              }))}
                              className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className={`${isMobile ? 'p-4' : 'p-5'} border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3`}>
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => setShowNewTaskModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                  onClick={handleCreateTask}
                >
                  Create Task
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Implementation Task Modal — shared hours-pool form (department picker, time stepper, attachments) */}
        {implModal && (
          <DepartmentTaskTimeModal
            isOpen
            onClose={() => {
              setImplModal(null);
              taskTime.refresh?.();
            }}
            taskTime={taskTime}
            initialTab={implModal.tab}
          />
        )}

        {/* View/Edit Task Modal */}
        {showTaskModal && selectedTask && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeTaskModal}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={closeTaskModal}
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                    selectedTask.completed
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                      : 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                  }`}>
                    {selectedTask.completed ? <CheckCircle2 className="w-5 h-5" /> : <ListTodo className="w-5 h-5" />}
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Task Details</h2>
                  <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    selectedTask.completed
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  }`}>
                    {selectedTask.completed ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {selectedTask.completed ? 'Done' : 'To Do'}
                  </span>
                </div>
              </div>

              {/* Modal Body */}
              <div className={isMobile ? 'p-4' : 'p-6'}>
                {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>}
                {/* Title Section */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">Title</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedTask.title}
                      onChange={(e) => setEditedTask({...editedTask, title: e.target.value})}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="text-lg font-medium text-gray-900 dark:text-white">{selectedTask.title}</div>
                  )}
                </div>

                {/* Description Section */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">Description</h3>
                  {isEditing ? (
                    <textarea
                      value={editedTask.description}
                      onChange={(e) => setEditedTask({...editedTask, description: e.target.value})}
                      className="w-full px-3 py-2.5 h-32 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    />
                  ) : (
                    <div className="text-sm whitespace-pre-wrap leading-relaxed text-gray-700 dark:text-gray-300">{selectedTask.description}</div>
                  )}
                </div>

                {/* Task Info Section */}
                <div className="mb-6 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4">
                  <div className="space-y-3">
                    {/* Due Date */}
                    {selectedTask.dueDate && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium w-24 shrink-0 text-gray-500 dark:text-gray-400">Due date:</span>
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {formatDueDate(selectedTask.dueDate)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Company */}
                    {selectedTask.companyName && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium w-24 shrink-0 text-gray-500 dark:text-gray-400">Company:</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{selectedTask.companyName}</span>
                      </div>
                    )}

                    {/* Created By */}
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium w-24 shrink-0 text-gray-500 dark:text-gray-400">Created by:</span>
                      <div className="flex items-center gap-2">
                        {isFathomTask(selectedTask) ? (
                          <>
                            <img src="/fathomlogo.png" alt="Fathom" className="w-6 h-6 rounded-full object-contain" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Fathom</span>
                          </>
                        ) : (() => {
                          if (selectedTask.createdBy === 'Internal') {
                            return (
                              <>
                                <UserAvatar
                                  user={{
                                    name: 'Internal',
                                    initials: 'I',
                                    type: 'internal'
                                  }}
                                  size="sm"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Internal</span>
                              </>
                            );
                          }

                          const internalUser = internalUsers.find(u => u.id === selectedTask.createdBy);
                          if (internalUser) {
                            return (
                              <>
                                <UserAvatar
                                  user={{
                                    name: internalUser.firstName && internalUser.lastName
                                      ? `${internalUser.firstName} ${internalUser.lastName}`
                                      : internalUser.firstName
                                      ? internalUser.firstName
                                      : internalUser.email?.split('@')[0] || 'Unknown',
                                    initials: internalUser.firstName && internalUser.lastName
                                      ? `${internalUser.firstName[0]}${internalUser.lastName[0]}`.toUpperCase()
                                      : internalUser.firstName
                                      ? internalUser.firstName.substring(0, 2).toUpperCase()
                                      : (internalUser.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
                                    profileImage: internalUser.profileImage
                                  }}
                                  size="sm"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {internalUser.firstName && internalUser.lastName
                                    ? `${internalUser.firstName} ${internalUser.lastName}`
                                    : internalUser.firstName
                                    ? internalUser.firstName
                                    : internalUser.email?.split('@')[0] || 'Unknown'}
                                </span>
                              </>
                            );
                          }

                          const clientUser = clientUsers[selectedTask.createdBy];
                          if (clientUser) {
                            return (
                              <>
                                <UserAvatar
                                  user={{
                                    name: clientUser.name,
                                    initials: clientUser.initials,
                                    type: 'client'
                                  }}
                                  size="sm"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{clientUser.name}</span>
                              </>
                            );
                          }

                          return (
                            <>
                              <UserAvatar
                                user={{
                                  name: 'Unknown',
                                  initials: 'U',
                                  type: 'unknown'
                                }}
                                size="sm"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">Unknown</span>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Estimated Time */}
                    {selectedTask.estimatedHours > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium w-24 shrink-0 text-gray-500 dark:text-gray-400">Estimated:</span>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {selectedTask.estimatedHours}h {selectedTask.estimatedMinutes}m
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>



                {/* Files Section */}
                {selectedTask.files && selectedTask.files.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">Attachments</h3>
                    <div className="space-y-2">
                      {selectedTask.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <Upload className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                            {file.fileUrl ? (
                              <a
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline truncate"
                              >
                                {file.fileName || file.name || 'Attachment'}
                              </a>
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300 truncate">{file.fileName || file.name || 'Attachment'}</span>
                            )}
                          </div>
                          {isEditing && (
                            <button
                              onClick={() => setEditedTask(prev => ({
                                ...prev,
                                files: prev.files.filter((_, i) => i !== index)
                              }))}
                              className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {isEditing && (
                  <div className="mt-4">
                    <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <Upload className="w-4 h-4" />
                      <span>Add more files</span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          setEditedTask({
                            ...editedTask,
                            files: [...editedTask.files, ...Array.from(e.target.files)]
                          });
                        }}
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className={`${isMobile ? 'p-4' : 'p-5'} border-t border-gray-200 dark:border-gray-800 flex justify-between items-center gap-3`}>
                <div>
                  {!isEditing && !selectedTask.completed && (
                    <button
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-colors"
                      onClick={(e) => handleToggleTaskCompletion(selectedTask.id, e)}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Complete
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  {isEditing ? (
                    <>
                      {selectedTask.createdBy === currentUser.uid && (
                        <button
                          className="px-4 py-2 text-sm font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                          onClick={() => handleDeleteTask(selectedTask.id)}
                        >
                          Delete
                        </button>
                      )}
                      <button
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                        onClick={handleSaveClick}
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Show Edit/Delete buttons only if user is the creator */}
                      {selectedTask.createdBy === currentUser.uid ? (
                        <>
                          <button
                            className="px-4 py-2 text-sm font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                            onClick={() => handleDeleteTask(selectedTask.id)}
                          >
                            Delete
                          </button>
                          <button
                            className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
                            onClick={handleEditClick}
                          >
                            Edit
                          </button>
                        </>
                      ) : (
                        /* For assigned tasks (not created by user) or onboarding tasks, only show Close button */
                        <button
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          onClick={() => {
                            setShowTaskModal(false);
                            setSelectedTask(null);
                          }}
                        >
                          Close
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalTasks;
